import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { X, Filter, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import SEO, { collectionSchema } from "@/components/SEO";

const STATUS_TAG = {
  advancing: { label: "Advancing", cls: "bb-tag-positive" },
  permitting: { label: "Permitting", cls: "bb-tag-neutral" },
  delayed: { label: "Delayed", cls: "bb-tag-negative" },
  operational: { label: "Operational", cls: "bb-tag-positive" },
};
const STATUSES = Object.keys(STATUS_TAG);

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const Chip = ({ label, active, onClick, testId, accent = "navy" }) => {
  const base =
    "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border transition-colors";
  const styles = active
    ? accent === "copper"
      ? "bg-bb-copper text-white border-bb-copper"
      : "bg-bb-navy text-white border-bb-navy"
    : "bg-white text-bb-navy border-border hover:border-bb-navy";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${styles}`}
      data-testid={testId}
      data-active={active}
    >
      {label}
      {active && <X size={11} strokeWidth={2.5} />}
    </button>
  );
};

export default function ProjectsListPage() {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const region = searchParams.get("region") || null;
  const commodity = searchParams.get("commodity") || null;
  const stage = searchParams.get("stage") || null;
  const status = searchParams.get("status") || null;

  const updateParam = (key, next) => {
    const params = new URLSearchParams(searchParams);
    if (!next || params.get(key) === next) params.delete(key);
    else params.set(key, next);
    setSearchParams(params, { replace: false });
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/projects"),
      api.get("/companies"),
      api.get("/regions"),
    ])
      .then(([p, c, r]) => {
        if (!mounted) return;
        if (Array.isArray(p.data)) setProjects(p.data);
        if (Array.isArray(c.data)) setCompanies(c.data);
        if (Array.isArray(r.data)) setRegions(r.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const companyLookup = useMemo(
    () => Object.fromEntries(companies.map((c) => [c.id, c])),
    [companies],
  );

  const stages = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => p.stage && set.add(p.stage));
    return Array.from(set).sort();
  }, [projects]);

  const commoditiesList = useMemo(() => {
    const set = new Set();
    projects.forEach((p) =>
      (p.commodity || []).forEach((c) => set.add(c)),
    );
    return Array.from(set).sort();
  }, [projects]);

  const filtered = useMemo(
    () =>
      projects.filter((p) => {
        if (region && p.region !== region) return false;
        if (commodity && !(p.commodity || []).includes(commodity)) return false;
        if (stage && p.stage !== stage) return false;
        if (status && p.status !== status) return false;
        return true;
      }),
    [projects, region, commodity, stage, status],
  );

  const activeCount =
    (region ? 1 : 0) + (commodity ? 1 : 0) + (stage ? 1 : 0) + (status ? 1 : 0);

  const clearAll = () =>
    setSearchParams(new URLSearchParams(), { replace: false });

  return (
    <div data-testid="projects-list-page">
      <SEO
        title="Projects"
        description="A searchable database of Canadian mining projects — capex, permits, stages, and Indigenous partnership status across all Canadian jurisdictions."
        jsonLd={collectionSchema("Projects", "Canadian mining projects database", "/projects")}
      />
      {/* Header */}
      <section className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="bb-overline text-bb-copper">
                Project Watch · Database
              </div>
              <h1
                className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
                data-testid="projects-page-headline"
              >
                Projects
              </h1>
              <p className="mt-5 text-base lg:text-lg text-bb-slate max-w-2xl leading-relaxed">
                A searchable database of Canadian mining projects — from
                grassroots exploration through construction and operation.
                Filter by region, commodity, stage, or status.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="bb-overline text-bb-slate">
                {filtered.length}{" "}
                {filtered.length === 1 ? "project" : "projects"}
              </span>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="bb-btn-outline inline-flex items-center gap-2 px-4 py-2 text-xs"
                  data-testid="projects-clear-filters"
                >
                  <X size={14} strokeWidth={2.4} />
                  Clear filters ({activeCount})
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section
        className="bg-bb-offwhite border-b border-border sticky top-16 z-30"
        data-testid="projects-filter-bar"
      >
        <div className="bb-container py-5 space-y-3">
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <Filter size={13} className="text-bb-slate" />
              <span className="bb-overline text-bb-slate">Region</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {regions.map((r) => (
                <Chip
                  key={r.id}
                  label={r.name}
                  active={region === r.id}
                  onClick={() => updateParam("region", r.id)}
                  testId={`chip-region-${r.slug}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Commodity</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {commoditiesList.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  active={commodity === c}
                  onClick={() => updateParam("commodity", c)}
                  testId={`chip-commodity-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  accent="copper"
                />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Stage</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {stages.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  active={stage === s}
                  onClick={() => updateParam("stage", s)}
                  testId={`chip-stage-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Status</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <Chip
                  key={s}
                  label={STATUS_TAG[s].label}
                  active={status === s}
                  onClick={() => updateParam("status", s)}
                  testId={`chip-status-${s}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 lg:py-16">
        <div className="bb-container">
          {loading ? (
            <div className="text-center py-20 bb-overline text-bb-slate">
              Loading the database…
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="border border-border bg-white p-12 text-center"
              data-testid="projects-empty-state"
            >
              <div className="bb-overline text-bb-copper">No matches</div>
              <h3 className="font-archivo text-2xl text-bb-navy mt-3 font-semibold">
                No projects match those filters.
              </h3>
              <button
                type="button"
                onClick={clearAll}
                className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
                data-testid="projects-empty-clear"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div
              className="bg-white border border-border overflow-x-auto"
              data-testid="projects-table"
            >
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border bg-bb-offwhite">
                    <th className="p-4 bb-overline text-bb-slate">Project</th>
                    <th className="p-4 bb-overline text-bb-slate">Province</th>
                    <th className="p-4 bb-overline text-bb-slate">
                      Commodity
                    </th>
                    <th className="p-4 bb-overline text-bb-slate">Stage</th>
                    <th className="p-4 bb-overline text-bb-slate">Status</th>
                    <th className="p-4 bb-overline text-bb-slate">Capex</th>
                    <th className="p-4 bb-overline text-bb-slate">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const tag = STATUS_TAG[p.status] || STATUS_TAG.advancing;
                    const co = companyLookup[p.company];
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-border last:border-b-0 hover:bg-bb-offwhite transition-colors"
                        data-testid={`projects-row-${p.slug}`}
                      >
                        <td className="p-4 align-top">
                          <div className="font-archivo font-semibold text-bb-navy text-base leading-tight">
                            <Link
                              to={`/projects/${p.slug}`}
                              className="bb-link"
                            >
                              {p.name}
                            </Link>
                          </div>
                          <div className="text-xs text-bb-slate mt-1 font-mono">
                            {co ? co.name : p.company}
                          </div>
                        </td>
                        <td className="p-4 align-top">
                          <span className="inline-flex items-center gap-1.5 text-xs font-mono text-bb-slate">
                            <MapPin size={12} /> {p.province}
                          </span>
                        </td>
                        <td className="p-4 align-top text-xs font-mono text-bb-slate">
                          {(p.commodity || []).join(" · ")}
                        </td>
                        <td className="p-4 align-top">
                          <div className="bb-overline text-bb-navy">
                            {p.stage}
                          </div>
                        </td>
                        <td className="p-4 align-top">
                          <span
                            className={`text-[11px] font-mono px-2 py-1 ${tag.cls}`}
                          >
                            {tag.label}
                          </span>
                        </td>
                        <td className="p-4 align-top font-mono text-sm text-bb-navy">
                          {p.capexEstimate
                            ? `$${p.capexEstimate.toLocaleString()}M`
                            : "—"}
                        </td>
                        <td className="p-4 align-top text-xs font-mono text-bb-slate">
                          {formatDate(p.latestUpdate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
